import { useTranslation } from "react-i18next";

import { Box, CircularProgress, Typography } from "@mui/material";

import { FormDialog } from "../../../../form-dialog/FormDialog";
import { ProceedingParticipantResponse, ProceedingParticipantType } from "../../../api/ClientFileApi";
import { useClientFileContext } from "../../../context/ClientFileContext";
import { ParticipantFormFields } from "../../../participants/participant-form-fields/ParticipantFormFields";
import { useClientSpecificParticipant } from "./hooks/useClientSpecificParticipant";

interface Props {
  clientId: string;
  /** The types the consuming field offers; a single entry is preselected and locked. */
  allowedTypes: ProceedingParticipantType[];
  prefilledName?: string;
  editParticipantId?: string;
  onCreated: (participant: ProceedingParticipantResponse) => void;
  onClose: () => void;
}

export function ClientSpecificParticipantDialog({
  clientId,
  allowedTypes,
  prefilledName,
  editParticipantId,
  onCreated,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const { capabilities } = useClientFileContext();
  const manageGlobalParticipants = capabilities.manageGlobalParticipants;
  const isEditing = !!editParticipantId;

  const { formik, isLoading } = useClientSpecificParticipant({
    clientId,
    allowedTypes,
    prefilledName,
    editParticipantId,
    onCreated: (created) => onCreated(created),
    onClose,
  });

  return (
    <FormDialog
      title={
        isEditing
          ? t("proceedingParticipants.edit.dialogTitle")
          : t("proceedingParticipants.create.clientSpecificDialogTitle")
      }
      onClose={onClose}
      formik={formik}
      submitLabel={t("common.save")}
      submitDisabled={isEditing && !formik.dirty}
      paperSx={{ width: 960 }}
    >
      {isEditing && isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {!isEditing && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t("proceedingParticipants.create.clientSpecificSubtitle")}
            </Typography>
          )}
          <ParticipantFormFields
            formik={formik}
            allowedTypes={allowedTypes}
            typeDisabled={isEditing || allowedTypes.length === 1}
            showAlsoCreateGlobalCheckbox={!isEditing && manageGlobalParticipants}
          />
        </>
      )}
    </FormDialog>
  );
}
